import { useCallback, useMemo } from "react";
import { UseObjectNavigationArgs, NavigableObject } from "./types";

export function useObjectNavigation<T extends NavigableObject>({
  objects,
  selectedId,
  onSelectId,
  onAfterSelect,
  wrap = false,
}: UseObjectNavigationArgs<T>) {
  const selectedIndex = useMemo(() => {
    if (!selectedId) return -1;
    return objects.findIndex((o) => o.id === selectedId);
  }, [objects, selectedId]);

  const total = objects.length;

  const canGoPrev = wrap ? total > 1 : selectedIndex > 0;
  const canGoNext = wrap
    ? total > 1
    : selectedIndex >= 0 && selectedIndex < total - 1;

  const goToIndex = useCallback(
    (idx: number) => {
      const obj = objects[idx];
      if (!obj) return;

      onSelectId?.(obj.id);

      if (onAfterSelect) {
        // Let parent state update first (common for "selectedObject" prop patterns)
        setTimeout(() => onAfterSelect(obj), 0);
      }
    },
    [objects, onAfterSelect, onSelectId],
  );

  const goPrev = useCallback(() => {
    if (total === 0) return;

    if (selectedIndex === -1) {
      // If nothing selected, pick first (or last if you prefer)
      goToIndex(0);
      return;
    }

    if (wrap) {
      const idx = (selectedIndex - 1 + total) % total;
      goToIndex(idx);
      return;
    }

    if (selectedIndex > 0) goToIndex(selectedIndex - 1);
  }, [goToIndex, selectedIndex, total, wrap]);

  const goNext = useCallback(() => {
    if (total === 0) return;

    if (selectedIndex === -1) {
      goToIndex(0);
      return;
    }

    if (wrap) {
      const idx = (selectedIndex + 1) % total;
      goToIndex(idx);
      return;
    }

    if (selectedIndex < total - 1) goToIndex(selectedIndex + 1);
  }, [goToIndex, selectedIndex, total, wrap]);

  return {
    selectedIndex,
    total,
    canGoPrev,
    canGoNext,
    goPrev,
    goNext,
    goToIndex,
  };
}
